import { type JSX, useMemo, useRef } from 'react';

import { DataTableGanttAnnotationIndicators } from './DataTableGanttAnnotationsIndicators.js';
import * as styles from './DataTableGanttChartCell.sty.js';
import { DataTableGanttChartGrid } from './DataTableGanttChartGrid.js';
import { useMinMaxFromConfig } from './hooks/useMinMaxFromConfig.js';
import {
  GanttChartStateContext,
  ganttChartStateInitialState,
} from '../../../../charts/gantt-chart/context/GanttChartStateContext.js';
import { GanttChartLine } from '../../../../charts/gantt-chart/GanttChartLine.js';
import type { GanttSegmentData } from '../../../../charts/gantt-chart/types/gantt-chart.js';
import { useContainerSize } from '../../../../core/hooks/useContainerSize.js';
import { createStore } from '../../../../core/store/store.js';
import { DataTableDefaultCell } from '../../components/DataTableDefaultCell.js';
import { useDataTable } from '../../contexts/DataTableContext.js';
import type { DataTableGanttColumnConfig } from '../ColumnTypes/column-types-types.js';

/**
 * TypeGuard to check for GanttSegmentData
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isGanttSegmentData(value: any): value is GanttSegmentData {
  return (
    typeof value === 'object' &&
    typeof value.start === 'number' &&
    (value.end === undefined || typeof value.end === 'number')
  );
}

type DataTableGanttChartCellProps = Readonly<{
  data: GanttSegmentData | GanttSegmentData[];
  parentData?: GanttSegmentData | GanttSegmentData[];
  columnId: string;
  config: DataTableGanttColumnConfig;
  isLastPageRow: boolean;
}>;

/**
 * @internal
 */
export const DataTableGanttChartCell = ({
  data,
  parentData,
  columnId,
  config,
  isLastPageRow,
}: Readonly<DataTableGanttChartCellProps>): JSX.Element => {
  const table = useDataTable();
  const { min, max } = useMinMaxFromConfig(columnId);

  const segmentsData = useMemo(
    () => (Array.isArray(data) ? data : [data]),
    [data],
  );
  const parentSegmentsData = useMemo(
    () =>
      parentData === undefined || Array.isArray(parentData)
        ? parentData
        : [parentData],
    [parentData],
  );

  const lineRef = useRef(null);
  const wrapperRef = useRef(null);
  const { width } = useContainerSize(lineRef);
  const { height } = useContainerSize(wrapperRef);

  // Create a separate store instance for each gantt chart cell to isolate state
  const stateStore = useRef(createStore(ganttChartStateInitialState)).current;

  return (
    <div ref={wrapperRef} className={styles.ganttChartCell}>
      <DataTableGanttChartGrid
        width={width}
        height={height}
        min={min}
        max={max}
        config={config}
        isSubrowIndicatorColumn={
          table.getSubRowIndicatorColumnId() === columnId
        }
      />
      <DataTableDefaultCell className={styles.ganttChartDefaultCell}>
        <GanttChartStateContext.Provider value={stateStore}>
          <GanttChartLine
            ref={lineRef}
            min={min}
            max={max}
            segmentsData={segmentsData}
            parentSegmentsData={parentSegmentsData}
            size={table.atoms.visualVariantConfig?.get().rowDensity}
            config={config}
          />
        </GanttChartStateContext.Provider>
      </DataTableDefaultCell>
      <DataTableGanttAnnotationIndicators
        width={width}
        height={height}
        min={min}
        max={max}
        config={config}
        columnId={columnId}
        isLastPageRow={isLastPageRow}
      />
    </div>
  );
};
